"use client";
import useTranslate from "@app/_hooks/useTranslate";
import { useMarket } from "@context/marketContext";
import { useSiteInfo } from "@context/siteInfoContext";
import { XIcon } from "@ngg/icons";
import type { CartItem } from "@server/gql/graphql";

import { cn } from "@/lib/utils";
import { getEstimatedShipDate } from "@/utils/cart/getEstimatedShipDate";
import { isMadeToOrderCartItem } from "@/utils/product/isMadeToOrderCartItem";

import Stock from "../Stock/Stock";
import CheckoutItemAction from "./CheckoutItemAction";
import CheckoutItemBadge from "./CheckoutItemBadge";
import CheckoutItemImage from "./CheckoutItemImage";
import CheckoutItemItems from "./CheckoutItemItems";
import CheckoutItemName from "./CheckoutItemName";
import CheckoutItemPrice from "./CheckoutItemPrice";
import CheckoutItemTotal from "./CheckoutItemTotal";
import StockWatchStatus from "./StockWatchStatus";

const CheckoutItem = ({
  cartItem,
  loading,
  removeItem,
  updateItem,
}: {
  cartItem: CartItem;
  loading?: boolean;
  removeItem: (item: CartItem) => void;
  updateItem: (item: CartItem) => void;
}) => {
  const t = useTranslate();
  const { locale, contentfulLocale } = useMarket().state.market;
  const { showCheckoutMadeToOrderDisclaimer } = useSiteInfo();

  // For a made-to-order line, `DeliveryItem` appends the estimated ship date
  // after its status. Compute it per item from the line's own `deliveryTimeDays`.
  // Gated on the SiteInfo kill-switch (stakeholder-controlled; positive "show"
  // flag) and `isMadeToOrderCartItem` so it stays `undefined` otherwise — that
  // keeps in-stock lines unchanged, drops the "…, skickas tidigast" clause when
  // the disclaimer is off, and prevents the value from leaking onto a DOM node
  // via a non-DeliveryItem status component's `{...props}`.
  const madeToOrderShipDate =
    showCheckoutMadeToOrderDisclaimer && isMadeToOrderCartItem(cartItem)
      ? getEstimatedShipDate(cartItem.deliveryTimeDays, contentfulLocale)
      : undefined;

  const [discountCode] =
    cartItem.appliedPromotions?.map((promotion) => promotion.discountCode) ??
    [];
  return (
    <div
      className={cn(
        "relative col-span-full grid grid-cols-[7rem,1fr] grid-rows-[1fr,auto] text-base lg:grid-cols-[8rem,1fr]",
      )}
    >
      <CheckoutItemImage
        slug={cartItem.extras?.product_url}
        locale={locale}
        src={cartItem.imageKey}
        alt={cartItem.name}
        className="block h-full min-h-28 w-28 flex-shrink-0 lg:row-span-2 lg:h-auto lg:min-h-32 lg:w-32"
      />
      <div className="relative grid grid-flow-row items-center gap-y-1 overflow-hidden px-3 py-2 lg:grid-flow-row lg:grid-cols-[3fr_1fr_1fr_1fr] lg:grid-rows-[1fr,auto]">
        <div className="relative contents w-full flex-col items-start gap-1 place-self-start overflow-hidden lg:flex">
          <CheckoutItemName
            className="w-full overflow-hidden"
            locale={locale}
            name={cartItem.name}
            variant={cartItem.extras?.variant}
            slug={cartItem.extras?.product_url}
            brand={cartItem.extras?.brand}
          />
          {cartItem.stockWatchStatus?.ableToComplete && (
            <>
              <Stock
                type="checkout"
                item={cartItem}
                madeToOrderShipDate={madeToOrderShipDate}
              />
              <CheckoutItemItems
                items={cartItem.underlyingItems}
                locale={locale}
              />
            </>
          )}
        </div>
        <CheckoutItemAction
          item={cartItem}
          loading={loading}
          update={updateItem}
        />
        <CheckoutItemPrice
          prefix={t("pricePerItem")}
          price={cartItem.price}
          priceOriginal={cartItem.priceOriginal}
          locale={locale}
        />
        <CheckoutItemTotal
          prefix={t("total")}
          amount={cartItem.totalGrossAmount}
          locale={locale}
        />
        {discountCode && (
          <CheckoutItemBadge type="discount">
            {t("discountCode")}: {discountCode}
          </CheckoutItemBadge>
        )}
      </div>
      <StockWatchStatus
        stock={cartItem.stockWatchStatus}
        onRemove={() => removeItem(cartItem)}
        onUpdate={(quantity: number) => updateItem({ ...cartItem, quantity })}
        className="col-span-full lg:col-span-1 lg:m-2"
      />
      {cartItem.stockWatchStatus?.ableToComplete && (
        <button
          type="button"
          className="absolute right-1 top-1 bg-transparent p-2 lg:bottom-1 lg:top-auto lg:p-1"
          onClick={() => removeItem(cartItem)}
        >
          <XIcon className="h-4 w-4 text-grey-300 lg:hidden" />
          <span className="hidden text-sm uppercase underline lg:block">
            {t("remove")}
          </span>
        </button>
      )}
    </div>
  );
};

CheckoutItem.displayName = "CheckoutItem";

export default CheckoutItem;
